import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const WebsiteAccessibilityPage = () => {
  return (
    <div className="pt-20">
      <SEO
        title="Website Accessibility Statement"
        description="Brunswick Glass is committed to ensuring digital accessibility for people with disabilities. Learn about our accessibility standards and ongoing improvement efforts."
        keywords="accessibility, WCAG, ADA compliance, website accessibility, Brunswick Glass"
      />
      
      {/* Hero Section */}
      <section className="relative bg-[#F9F9F7]">
        <div className="section-container py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4B5563] font-body mb-6 hover:text-[#0F4C81] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl md:text-5xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-4"
            >
              Website Accessibility Statement
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-lg text-[#4B5563]"
            >
              Last Updated: February 27, 2026
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-10"
            >
              {/* Our Commitment */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Our Commitment to Accessibility</h2>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
                </p>
              </motion.div>

              {/* Accessibility Standards */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Accessibility Standards</h2>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
                </p>
              </motion.div>

              {/* Accessibility Technology & Tools */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Accessibility Technology & Tools</h2>
                <p className="font-body text-[#4B5563] leading-relaxed mb-4">
                  To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
                </p>
                <ul className="space-y-2 font-body text-[#4B5563]">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Screen reader enhancements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Keyboard navigation improvements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Color contrast adjustments
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Text size and spacing controls
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Focus indicators
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Additional accessibility interface options
                  </li>
                </ul>
                <p className="font-body text-[#4B5563] leading-relaxed mt-4">
                  This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
                </p>
              </motion.div>

              {/* Ongoing Improvement */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Ongoing Improvement</h2>
                <p className="font-body text-[#4B5563] leading-relaxed mb-4">
                  Accessibility is an ongoing initiative. We are:
                </p>
                <ul className="space-y-2 font-body text-[#4B5563]">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Reviewing site content and functionality regularly
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Working with developers and accessibility tools
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Identifying and remediating barriers
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Incorporating accessibility into future design and development
                  </li>
                </ul>
                <p className="font-body text-[#4B5563] leading-relaxed mt-4">
                  We understand accessibility is a continuous journey and are committed to making improvements over time.
                </p>
              </motion.div>

              {/* Third-Party Content */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Third-Party Content</h2>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
                </p>
              </motion.div>

              {/* Feedback & Assistance */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Feedback & Assistance</h2>
                <p className="font-body text-[#4B5563] leading-relaxed mb-4">
                  If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us.
                </p>
                <p className="font-body text-[#4B5563] leading-relaxed mb-4">
                  Please include:
                </p>
                <ul className="space-y-2 font-body text-[#4B5563] mb-4">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    The page URL
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Description of the issue
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full mt-2 flex-shrink-0"></span>
                    Your preferred contact method
                  </li>
                </ul>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  <strong>Contact:</strong> Information available on our <Link to="/contact" className="text-[#0F4C81] hover:underline">Contact Us Page</Link>
                </p>
                <p className="font-body text-[#4B5563] leading-relaxed mt-4">
                  We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
                </p>
              </motion.div>

              {/* Good Faith Efforts */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Good Faith Efforts</h2>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
                </p>
              </motion.div>

              {/* Terms & Conditions */}
              <motion.div variants={fadeInUp}>
                <h2 className="font-heading text-2xl font-medium text-[#1A202C] mb-4">Terms & Conditions — Accessibility Clause</h2>
                <p className="font-body text-[#4B5563] leading-relaxed">
                  We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebsiteAccessibilityPage;
